import { useCallback, useEffect, useRef, useState } from 'react';
import useObservationActivity, { observationAvailability } from './useObservationActivity';

// Finish one read before scheduling another; ignore completions from old scopes.
export default function useBoundedPolling({ enabled, scope, interval, fetch, onData, onError }) {
  const activity = useObservationActivity();
  const resumeActivity = activity.resume;
  const callbacks = useRef({ fetch, onData, onError });
  callbacks.current = { fetch, onData, onError };
  const flight = useRef(false);
  const [failure, setFailure] = useState(null);
  const [lastReadAt, setLastReadAt] = useState(null);
  const [refreshEpoch, setRefreshEpoch] = useState(0);
  useEffect(() => { setFailure(null); setLastReadAt(null); }, [scope]);
  const resume = useCallback(() => { setFailure(null); resumeActivity(); setRefreshEpoch(value => value + 1); }, [resumeActivity]);
  useEffect(() => {
    if (!enabled || !activity.active || failure) return undefined;
    let cancelled = false;
    let timer;
    let errors = 0;
    const poll = async () => {
      if (cancelled || observationAvailability()) return;
      if (flight.current) { timer = setTimeout(poll, interval); return; }
      flight.current = true;
      let stopped = false;
      try {
        const data = await callbacks.current.fetch();
        if (cancelled || observationAvailability()) return;
        callbacks.current.onData(data);
        errors = 0;
        setLastReadAt(Date.now());
      } catch (error) {
        if (cancelled) return;
        errors += 1;
        stopped = [401, 403].includes(error?.status) || errors >= 3;
        if (stopped) {
          setFailure([401, 403].includes(error?.status) ? 'auth' : 'failure');
          callbacks.current.onError?.(error);
        }
      } finally {
        flight.current = false;
        if (!cancelled && !stopped) timer = setTimeout(poll, interval);
      }
    };
    timer = setTimeout(poll, refreshEpoch ? 0 : interval);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled, scope, interval, activity.active, failure, refreshEpoch]);
  return { reason: !enabled ? 'unavailable' : failure || activity.reason, lastReadAt, resume };
}
